import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { Contact, Customer, GuidId, Operation, Person, TaxPartner } from '@app/core/stingray-api/models';
import { PersonsService } from '@app/core/stingray-api/services';
import moment from 'moment';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoadingButtonComponent } from '../../shared/loading-button/loading-button.component';
import { MatButton } from '@angular/material/button';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDivider } from '@angular/material/divider';
import { CustomerAutocompleteComponent } from '../../shared/customer-autocomplete/customer-autocomplete.component';

import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'unite-edit-tax-partner-dialog',
    templateUrl: './edit-tax-partner-dialog.component.html',
    styleUrls: ['./edit-tax-partner-dialog.component.scss'],
    imports: [MatDialogTitle, TranslateModule, CustomerAutocompleteComponent, MatDivider, MatDialogContent, MatProgressSpinner, ReactiveFormsModule, MatRadioGroup, MatRadioButton, MatFormField, MatLabel, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, MatDialogActions, MatButton, MatDialogClose, LoadingButtonComponent]
})
export class EditTaxPartnerDialogComponent implements OnInit {

  isLoading: boolean;
  personGroup: UntypedFormGroup;
  searchGroup: UntypedFormGroup;
  isSaving: boolean;
  person: Person;

  constructor(
    private el: ElementRef,
    private matDialogRef: MatDialogRef<EditTaxPartnerDialogComponent>,
    private personsService: PersonsService,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: Contact) { }

  ngOnInit(): void {


    this.searchGroup = this.formBuilder.group({ customer: [] });
    this.searchGroup.get('customer').valueChanges.subscribe((customer: Customer) => {
      if (customer && customer.id) {
        this.loadPerson(customer.id);
      }
    });

    this.personGroup = this.formBuilder.group({
      gender: [, [Validators.required]],
      initials: [, [Validators.required]],
      firstName: [, [Validators.required]],
      middleName: [, []],
      surname: [, [Validators.required]],
      email: [, [Validators.required, Validators.email]],
      birthdate: [, [Validators.required]]
    });

    if (this.data) {
      this.loadPerson(this.data.id);
    }
  }
  save(): void {
    this.personGroup.markAllAsTouched();
    if (this.personGroup.valid) {
      this.isSaving = true;
      const isCreateAction = !this.person;
      const saveAction = isCreateAction ? this.create() : this.patch();
      saveAction
        .pipe(finalize(() => this.isSaving = false))
        .subscribe(person => {
          this.matDialogRef.close({
            id: person.id,
            name: person.name,
          } as TaxPartner);
        });
    } else {
      this.scrollToFirstInvalidControl();
    }
  }

  private loadPerson(id: string): void {
    this.isLoading = true;
    this.personsService
      .getPersonById({ id })
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(person => {
        this.person = person;
        this.personGroup.patchValue(person);

        if (person.birthdate) {
          this.personGroup.get('birthdate').setValue(moment(person.birthdate));
        }
      });
  }

  private scrollToFirstInvalidControl(): void {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector('form .ng-invalid');
    firstInvalidControl?.scrollIntoView();
  }

  private create(): Observable<Person> {

    return new Observable<Person>(obj => {

      const person = <Person>this.personGroup.value;

      if (person.birthdate) {
        person.birthdate = (<moment.Moment>this.personGroup.value.birthdate).locale('en').format('L');
      }

      this.personsService.addPerson({ body: person }).pipe(finalize(() => {
        this.isSaving = false;
      }))
        .pipe(finalize(() => this.isSaving = false))
        .subscribe((response: GuidId) => {
          person.id = response.id;
          person.name = `${this.personGroup.get('surname').value}, ${this.personGroup.get('initials').value}  ${this.personGroup.get('middleName').value ?? ''}`;

          obj.next(person);
          obj.complete();

        });

    });
  }



  private patch(): Observable<Person> {

    return new Observable<Person>(obj => {

      // Some magic to build patch operations
      const operations: Operation[] = [];
      Object.keys(this.personGroup.controls).forEach((key: string) => {
        const control = <FormControl>this.personGroup.get(key);
        if (control.value) {

          let value = control.value;
          if (key === 'birthdate' && control.value) {
            value = (<moment.Moment>control.value).locale('en').format('L');
          }


          operations.push({
            path: key,
            value: value,
            op: 'replace'
          });
        }
      });

      this.personsService
        .patchPerson({ id: this.person.id, body: operations })
        .pipe(finalize(() => this.isSaving = false))
        .subscribe(x => {
          obj.next(x);
          obj.complete();
        });
    });

  }
}
